import { useEffect, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { useTranslation } from 'react-i18next'
import SocietyForm from '../../Societies/SocietyForm'
import DocumentPreviewDialog from './DocumentPreviewDialog'
import { getSocietyLiteralDocument, generateSocietyLiteralDocumentUrl } from '../../../../services/societyDocuments'

const EMPTY_SOCIETY = { language: 'es', current_status: 'active' }

function normalizeSociety(society) {
  if (!society?.constitution_date) return society
  const parsed = new Date(society.constitution_date)
  if (isNaN(parsed)) return society
  return { ...society, constitution_date: parsed.toISOString().split('T')[0] }
}

export default function SocietyFormDialog({ open, initialValue, onClose, onSave }) {
  const { t } = useTranslation()
  const [value, setValue] = useState(EMPTY_SOCIETY)
  const [errors, setErrors] = useState({})
  const [literalFile, setLiteralFile] = useState(null)
  const [existingLiteralDoc, setExistingLiteralDoc] = useState(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const isEdit = !!initialValue?.id

  useEffect(() => {
    if (open) {
      setValue(normalizeSociety(initialValue) || EMPTY_SOCIETY)
      setErrors({})
      setLiteralFile(null)
      setExistingLiteralDoc(null)
      if (initialValue?.id) {
        getSocietyLiteralDocument(initialValue.id)
          .then((doc) => setExistingLiteralDoc(doc || null))
          .catch(() => setExistingLiteralDoc(null))
      }
    }
  }, [open, initialValue])

  const handleSave = async () => {
    const newErrors = {
      legal_id: !value.legal_id,
      type: !value.type
    }
    setErrors(newErrors)
    if (newErrors.legal_id || newErrors.type) return

    setSaving(true)
    try {
      await onSave(value, literalFile)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{isEdit ? t('edit_legal_entity') : t('add_legal_entity')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <SocietyForm value={value} onChange={setValue} errors={errors} />

        <Box>
          <Typography variant='caption' color='text.secondary' display='block'>
            {t('society_literal_document')}
          </Typography>
          <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 0.5 }}>
            {t('society_literal_document_helper')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Button component='label' size='small' variant='outlined' startIcon={<UploadFileIcon />}>
              {literalFile
                ? literalFile.name
                : existingLiteralDoc
                  ? t('Replace')
                  : t('Upload new')}
              <input
                key={literalFile ? literalFile.name : 'empty'}
                type='file'
                hidden
                accept='.pdf,.doc,.docx,.png,.jpg,.jpeg'
                onChange={(e) => setLiteralFile(e.target.files[0] || null)}
              />
            </Button>
            {existingLiteralDoc && !literalFile && (
              <Button size='small' onClick={() => setPreviewOpen(true)}>
                {t('Preview')}
              </Button>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Cancel')}</Button>
        <Button
          variant='contained'
          disabled={saving}
          onClick={handleSave}
          sx={{
            backgroundColor: window.$brand === 'PRB' ? '#17afb6' : '#102a46',
            borderRadius: window.$brand === 'PRB' ? 0 : 5
          }}
        >
          {t('Save')}
        </Button>
      </DialogActions>

      <DocumentPreviewDialog
        open={previewOpen}
        doc={existingLiteralDoc ? { id: initialValue?.id, filename: existingLiteralDoc.filename } : null}
        fetchUrl={(d) => generateSocietyLiteralDocumentUrl(d.id)}
        onClose={() => setPreviewOpen(false)}
      />
    </Dialog>
  )
}
